import { RelationType } from "Infrastructure/Api/Api";

/**
 * Whitelist of relations, that are valid business (LegalEntity) roles. Excluding relations like BeneficialOwner, ContactPerson, HuntersHunter, HuntersMerchant, ...
 *
 * @see {@link RelationType} from Infrastructure/Api/Api
 */

export const BUSINESS_ROLE_RELATION_TYPES = [
  RelationType.StatutoryBody,
  RelationType.ProxyHolder,
  RelationType.Governor,
  RelationType.InsolvencyAdministrator,
  RelationType.Liquidator,
  RelationType.ForcedAdministrator,
  RelationType.CityMayor,
  RelationType.Mayor,
  RelationType.BranchManager,
  RelationType.BranchOfficeManager,
] as const satisfies ReadonlyArray<RelationType>;

export type BusinessRelationType =
  (typeof BUSINESS_ROLE_RELATION_TYPES)[number];

/**
 * Type guard for the business-role whitelist. Widens to ReadonlyArray<RelationType>
 * for the membership check so any RelationType can be tested, while narrowing the
 * result to BusinessRelationType for callers.
 */
export const isBusinessRelationType = (
  relationType: RelationType,
): relationType is BusinessRelationType =>
  (BUSINESS_ROLE_RELATION_TYPES as ReadonlyArray<RelationType>).includes(
    relationType,
  );

export type RoleCategory = "statutoryBody" | "proxyHolder" | "other";

export const STATUTORY_BODY_ROLES: ReadonlyArray<RelationType> = [
  RelationType.StatutoryBody,
] as const;

export const PROXY_HOLDER_ROLES: ReadonlyArray<RelationType> = [
  RelationType.ProxyHolder,
] as const;

export const OTHER_ROLES: ReadonlyArray<RelationType> = [
  RelationType.Governor,
  RelationType.InsolvencyAdministrator,
  RelationType.Liquidator,
  RelationType.ForcedAdministrator,
  RelationType.CityMayor,
  RelationType.Mayor,
  RelationType.BranchManager,
  RelationType.BranchOfficeManager,
] as const;

export const ROLE_CATEGORY_MAP: Record<
  RoleCategory,
  ReadonlyArray<RelationType>
> = {
  statutoryBody: STATUTORY_BODY_ROLES,
  proxyHolder: PROXY_HOLDER_ROLES,
  other: OTHER_ROLES,
};

export const getRoleCategoryForRelationType = (
  relationType: RelationType,
): RoleCategory | undefined => {
  for (const [category, roles] of Object.entries(ROLE_CATEGORY_MAP)) {
    if (roles.includes(relationType)) {
      return category as RoleCategory;
    }
  }
  return undefined;
};
